import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

const BRAND = {
  burgundy: "#800020",
  bg: "#0b0b0c",
  card: "rgba(255,255,255,0.04)",
  border: "rgba(255,255,255,0.10)",
  text: "#f6f6f6",
  muted: "rgba(255,255,255,0.70)",
};

const styles = {
  wrap: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: 18,
    background: `
      radial-gradient(900px 500px at 15% 10%, rgba(128,0,32,0.35), transparent 60%),
      radial-gradient(700px 500px at 85% 20%, rgba(255,255,255,0.05), transparent 55%),
      ${BRAND.bg}
    `,
    color: BRAND.text,
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  },
  card: {
    width: "100%",
    maxWidth: 440,
    borderRadius: 18,
    border: `1px solid ${BRAND.border}`,
    background: BRAND.card,
    padding: 28,
    boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
    backdropFilter: "blur(8px)",
  },
  label: {
    fontSize: 12,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: BRAND.muted,
    fontWeight: 700,
  },
  input: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.35)",
    color: BRAND.text,
    outline: "none",
    fontSize: 14,
    marginTop: 8,
    boxSizing: "border-box",
  },
  btn: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: `linear-gradient(90deg, ${BRAND.burgundy}, rgba(128,0,32,0.65))`,
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 14,
  },
  errorBox: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,80,80,0.3)",
    background: "rgba(128,0,32,0.20)",
    color: "#fca5a5",
    fontSize: 13,
  },
  successBox: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,200,100,0.3)",
    background: "rgba(0,100,50,0.20)",
    color: "#6ee7b7",
    fontSize: 13,
  },
};

export default function Login({ user, onLogin }) {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Already logged in ? redirect
  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }

    setLoading(true);

    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    setLoading(false);

    if (authError) {
      setError("Incorrect email or password.");
      return;
    }

    // Pull the profile row that was created on signup
    const { data: profile } = await supabase
      .from("profiles")
      .select("id, display_name, role")
      .eq("id", data.user.id)
      .single();

    const authUser = {
      id: data.user.id,
      email: data.user.email,
      displayName: profile?.display_name ?? data.user.email,
      role: profile?.role ?? "member",
    };

    if (typeof onLogin === "function") onLogin(authUser);
    navigate("/dashboard");
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Africa Loud
        </div>

        <h1 style={{ margin: "10px 0 6px", fontSize: 22, fontWeight: 900 }}>
          Sign in
        </h1>

        <div style={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.5 }}>
          Use your team credentials to access the editorial dashboard.
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        <form onSubmit={handleSubmit} style={{ marginTop: 14 }}>
          <div>
            <div style={styles.label}>Email</div>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@africaloud.com"
              autoComplete="email"
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={styles.label}>Password</div>
            <input
              style={styles.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              type="password"
              autoComplete="current-password"
            />
          </div>

          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? "Signing in�" : "Sign in"}
          </button>
        </form>

        <div
          style={{
            marginTop: 16,
            fontSize: 13,
            color: BRAND.muted,
            textAlign: "center",
          }}
        >
          Don't have an account?{" "}
          <Link
            to="/signup"
            style={{ color: "#f9a8c9", fontWeight: 700, textDecoration: "none" }}
          >
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}